import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Heart, Loader2, Mic, Pause, Play, RotateCcw, Send, Square } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_S = 30;
const pickMime = () => ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const ERR = {
  audio_silent: "I couldn't hear anything. Try again a little closer to the phone.",
  audio_too_small: "That was a bit short. Say a few words and try again.",
  already_sent: "Your thank-you has already been sent.",
  own_gift: "This is your own gift, so there's no one to thank.",
};

// Recipient side: record one short thank-you (up to 30s), listen back, redo, then send.
export const GiftThanksRecorder = ({ slug, to = "", noteId = "", from = "", nudge = false, gold = "#D7BB7A", cream = "#F1ECDD" }) => {
  const [state, setState] = useState("loading"); // loading | idle | recording | review | sending | sent | error
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState("");
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState("");
  const [playing, setPlaying] = useState(false);
  const rec = useRef(null);
  const timer = useRef(null);
  const player = useRef(null);
  const box = useRef(null);
  const nudging = nudge && state === "idle";
  useEffect(() => { if (nudging) box.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [nudging]);

  useEffect(() => {
    const q = new URLSearchParams({ v: noteId, to });
    axios.get(`${API}/api/public/share/${slug}/thanks?${q}`).then((r) => setState(r.data.sent ? "sent" : "idle")).catch(() => setState("idle"));
    return () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); };
  }, [slug, noteId, to]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const stop = () => { clearInterval(timer.current); if (rec.current?.state === "recording") rec.current.stop(); };
  const start = async () => {
    setErr(""); player.current?.pause();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const b = new Blob(chunks, { type: r.mimeType || mime || "audio/webm" });
        setBlob(b); setUrl(URL.createObjectURL(b)); setState("review");
      };
      rec.current = r; r.start(); setSecs(0); setState("recording");
      timer.current = setInterval(() => setSecs((s) => { if (s + 1 >= MAX_S) stop(); return s + 1; }), 1000);
    } catch { setErr("Please allow the microphone to record your thank-you."); setState("error"); }
  };
  const send = async () => {
    setState("sending"); player.current?.pause();
    const fd = new FormData();
    fd.append("audio", blob, blob.type.includes("mp4") ? "thanks.m4a" : "thanks.webm");
    fd.append("to", to); fd.append("v", noteId);
    const tok = localStorage.getItem("reclaim_token");
    try {
      await axios.post(`${API}/api/public/share/${slug}/thanks`, fd, tok ? { headers: { Authorization: `Bearer ${tok}` } } : {});
      setState("sent");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(ERR[d] || "That didn't send. Please try again.");
      setState(d === "already_sent" ? "sent" : "review");
    }
  };
  const toggle = () => { const a = player.current; if (!a) return; if (playing) a.pause(); else a.play().catch(() => {}); };

  if (state === "loading") return null;
  const btn = "rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 transition-transform duration-200 active:scale-95";
  return (
    <div ref={box} className={`mt-6 rounded-2xl p-5 text-center transition-shadow duration-700 ${nudging ? "animate-[pulse_3s_ease-in-out_2]" : ""}`} style={{ border: `1px solid ${gold}${nudging ? "" : "55"}`, background: "rgba(241,236,221,0.03)", boxShadow: nudging ? `0 0 40px -10px ${gold}` : "none" }} data-testid="gift-thanks">
      {nudging && (
        <p className="mb-3 text-lg italic" style={{ color: cream, fontFamily: '"Cormorant Garamond", serif' }} data-testid="gift-thanks-nudge">
          Did that land? {from ? `${from} would love` : "They'd love"} to hear from you.
        </p>
      )}
      <p className="text-[0.6rem] uppercase tracking-[0.28em] inline-flex items-center gap-1.5" style={{ color: gold, fontFamily: '"Manrope", sans-serif' }}>
        <Heart size={11} /> Say thank you{from ? ` to ${from}` : ""}
      </p>
      {state === "sent" ? (
        <p className="mt-2 text-lg" style={{ color: cream, fontFamily: '"Cormorant Garamond", serif' }} data-testid="gift-thanks-sent">Your thank-you is on its way.</p>
      ) : (
        <>
          <p className="mt-2 text-base" style={{ color: cream, opacity: 0.8, fontFamily: '"Cormorant Garamond", serif' }}>
            {state === "review" ? "Listen back, then send it, or record again." : "Leave a short voice note back, up to 30 seconds."}
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
            {(state === "idle" || state === "error") && (
              <button type="button" onClick={start} className={btn} style={{ background: gold, color: "#0a132a" }} data-testid="gift-thanks-record"><Mic size={14} /> Record thank-you</button>
            )}
            {state === "recording" && (
              <button type="button" onClick={stop} className={btn} style={{ background: "#e9a39c", color: "#0a132a" }} data-testid="gift-thanks-stop"><Square size={13} /> Stop · {MAX_S - secs}s left</button>
            )}
            {(state === "review" || state === "sending") && (
              <>
                <button type="button" onClick={toggle} className={btn} style={{ border: `1px solid ${gold}`, color: gold }} data-testid="gift-thanks-play">
                  {playing ? <Pause size={13} /> : <Play size={13} />} {playing ? "Pause" : "Listen back"}
                </button>
                <button type="button" onClick={start} disabled={state === "sending"} className={btn} style={{ color: cream, opacity: 0.8 }} data-testid="gift-thanks-redo"><RotateCcw size={13} /> Redo</button>
                <button type="button" onClick={send} disabled={state === "sending"} className={btn} style={{ background: gold, color: "#0a132a" }} data-testid="gift-thanks-send">
                  {state === "sending" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
                </button>
                <audio ref={player} src={url} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
              </>
            )}
          </div>
        </>
      )}
      {err && <p className="mt-2 text-xs" style={{ color: "#e9a39c" }} data-testid="gift-thanks-error">{err}</p>}
    </div>
  );
};

const ThanksRow = ({ t, gold, cream }) => {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  const [heard, setHeard] = useState(t.listened);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const auth = { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` };
  const load = () => {
    setFailed(false);
    axios.get(`${API}/api/gift-thanks/${t.id}/audio`, { responseType: "blob", headers: auth })
      .then((r) => setSrc(URL.createObjectURL(r.data))).catch(() => setFailed(true));
  };
  useEffect(load, [t.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => src && URL.revokeObjectURL(src), [src]);
  const busy = !src && !failed;
  const play = () => {
    if (failed) { load(); return; }
    if (!src) return;
    if (playing) { ref.current.pause(); return; }
    ref.current.play().catch(() => {});
    if (!heard) { setHeard(true); axios.post(`${API}/api/gift-thanks/${t.id}/listened`, {}, { headers: auth }).catch(() => {}); }
  };
  return (
    <li className="flex items-center gap-3" data-testid={`thanks-item-${t.id}`}>
      <button type="button" onClick={play} aria-label="Play thank-you" className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform active:scale-95" style={{ background: gold, color: "#0a132a" }} data-testid={`thanks-play-${t.id}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : failed ? <RotateCcw size={14} /> : playing ? <Pause size={14} /> : <Play size={14} className="translate-x-[1px]" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate" style={{ color: cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}>
          {t.to || "Someone"} said thank you {!heard && <span className="ml-1 text-[0.55rem] uppercase tracking-[0.2em] align-middle" style={{ color: gold }} data-testid={`thanks-new-${t.id}`}>New</span>}
        </p>
        <p className="truncate text-xs" style={{ color: cream, opacity: 0.5 }}>{failed ? "Couldn't load, tap to try again" : `for “${t.title}” · ${Math.round(t.duration_seconds)}s`}</p>
      </div>
      {src && <audio ref={ref} src={src} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />}
    </li>
  );
};

// Sender side: thank-yous received for gifts, shown in the library.
export const GiftThanksInbox = ({ gold = "#D7BB7A", cream = "#F1ECDD" }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/gift-thanks/inbox`, { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } })
      .then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <div className="mb-6 rounded-2xl px-5 py-4" style={{ background: "rgba(245,224,173,0.04)", border: `1px solid ${gold}40` }} data-testid="thanks-inbox">
      <p className="mb-3 text-[0.6rem] uppercase tracking-[0.28em] inline-flex items-center gap-1.5" style={{ color: gold, fontFamily: '"Manrope", sans-serif' }}><Heart size={11} /> Thank-yous for your gifts</p>
      <ul className="space-y-3">{items.map((t) => <ThanksRow key={t.id} t={t} gold={gold} cream={cream} />)}</ul>
    </div>
  );
};
